import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GDLLogo } from './GDLLogo';
import { SectionCard, Badge } from './GDLComponents';
import GDLLayout from './GDLLayout';
import GDLAdminPanels from './GDLAdminPanels';
import './gdl.css';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function GDLAdmin() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [servants, setServants] = useState([]);
  const [simulating, setSimulating] = useState({});
  const [bulkLoading, setBulkLoading] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/api/demo/gdl/admin/stats`, { withCredentials: true }).then(r => setStats(r.data));
    axios.get(`${API}/api/demo/gdl/admin/civil-servants`, { withCredentials: true }).then(r => setServants(r.data));
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 3000); return () => clearInterval(iv); }, [load]);

  const simulateSalary = async (id) => {
    setSimulating(p => ({ ...p, [id]: true }));
    try {
      await axios.post(`${API}/api/demo/gdl/admin/simulate-salary/${id}`, {}, { withCredentials: true });
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Simulation failed'); }
    setSimulating(p => ({ ...p, [id]: false }));
  };

  const simulateAll = async () => {
    setBulkLoading(true);
    try { await axios.post(`${API}/api/demo/gdl/admin/simulate-all-salaries`, {}, { withCredentials: true }); load(); }
    catch (e) { alert('Bulk simulation failed'); }
    setBulkLoading(false);
  };

  const resetDemo = async () => {
    if (!window.confirm('This will clear ALL demo data and reseed with 9 civil servants. Continue?')) return;
    setResetting(true);
    try { await axios.post(`${API}/api/demo/gdl/admin/reset`, {}, { withCredentials: true }); load(); }
    catch (e) { alert('Reset failed'); }
    setResetting(false);
  };

  const getLoanHealth = (s) => {
    if (s.loan_status === 'completed') return { label: 'Completed', variant: 'grey' };
    if (s.loan_status === 'none') return { label: 'No loan', variant: 'grey' };
    if (!s.last_salary_date && s.months_paid === 0) return { label: 'No salary', variant: 'grey' };
    const days = s.last_salary_date ? (Date.now() - new Date(s.last_salary_date).getTime()) / 86400000 : 999;
    if (days > 35) return { label: 'Overdue', variant: 'error' };
    return { label: 'On Track', variant: 'success' };
  };

  const handleLogout = () => {
    localStorage.removeItem('gdl_demo_user');
    localStorage.removeItem('gdl_demo_token');
    nav('/demo/gdl/login');
  };

  const content = (
    <div style={{ minHeight: '100%', background: '#F4F5F7' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #B03140, #7A1E2B)', padding: '20px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: 1200, margin: '0 auto' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <GDLLogo height={24} showText={false} />
              <span style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>GDL+ Admin</span>
            </div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, marginTop: 4 }}>Civil Servant Finance Dashboard</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={resetDemo} disabled={resetting} data-testid="reset-demo-btn" style={{
              background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', padding: '8px 16px',
              borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>
              {resetting ? 'Resetting...' : 'Reset scenario'}
            </button>
            <button onClick={handleLogout} data-testid="admin-logout-btn" style={{
              background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', padding: '8px 16px',
              borderRadius: 6, fontSize: 12, cursor: 'pointer',
            }}>
              Logout
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px 16px' }}>
        {/* Stats */}
        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
            {[
              ['Total Civil Servants', stats.total_civil_servants, '#111827'],
              ['Total Salary Inflows', `NGN ${stats.total_salary_inflows.toLocaleString()}`, '#10B981'],
              ['Loan Repayments', `NGN ${stats.total_deductions_collected.toLocaleString()}`, '#B03140'],
              ['Outstanding Loans', `NGN ${stats.total_outstanding.toLocaleString()}`, '#EF4444'],
              ['Collection Rate', `${stats.collection_rate}%`, stats.collection_rate >= 90 ? '#10B981' : '#F59E0B'],
            ].map(([label, value, color]) => (
              <SectionCard key={label}>
                <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color }} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>{value}</div>
              </SectionCard>
            ))}
          </div>
        )}

        {/* Bulk Action */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>Civil Servants ({servants.length})</div>
          <button onClick={simulateAll} disabled={bulkLoading} data-testid="simulate-all-btn" style={{
            background: '#B03140', color: '#fff', border: 'none', padding: '10px 24px',
            borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer',
          }}>
            {bulkLoading ? 'Simulating...' : 'Simulate All Salaries'}
          </button>
        </div>

        {/* Table */}
        <div style={{ background: '#fff', borderRadius: 12, overflow: 'auto', border: '1px solid #E5E7EB' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
                {['Health', 'Name', 'State', 'Salary', 'Loan', 'Outstanding', 'Paid', 'Balance', 'Action'].map(h => (
                  <th key={h} style={{ padding: '12px 10px', fontWeight: 600, fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {servants.map(s => {
                const health = getLoanHealth(s);
                return (
                  <tr key={s.id} style={{ borderBottom: '1px solid #F3F4F6' }} data-testid={`row-${s.id}`}>
                    <td style={{ padding: '10px' }}><Badge variant={health.variant}>{health.label}</Badge></td>
                    <td style={{ padding: '10px', fontWeight: 600, whiteSpace: 'nowrap' }}>{s.full_name}</td>
                    <td style={{ padding: '10px', color: '#6B7280' }}>{s.state}</td>
                    <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>NGN {s.monthly_salary.toLocaleString()}</td>
                    <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>NGN {s.loan_principal.toLocaleString()}</td>
                    <td style={{ padding: '10px', fontWeight: 600, color: s.outstanding_balance > 0 ? '#EF4444' : '#10B981', whiteSpace: 'nowrap' }}>
                      NGN {s.outstanding_balance.toLocaleString()}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{s.months_paid}/12</td>
                    <td style={{ padding: '10px', fontWeight: 600, whiteSpace: 'nowrap' }}>NGN {s.wallet_balance.toLocaleString()}</td>
                    <td style={{ padding: '10px' }}>
                      {s.loan_status !== 'completed' ? (
                        <button onClick={() => simulateSalary(s.id)} disabled={simulating[s.id]}
                          data-testid={`simulate-${s.id}`}
                          style={{
                            background: simulating[s.id] ? '#E5E7EB' : '#B03140', color: simulating[s.id] ? '#6B7280' : '#fff',
                            border: 'none', padding: '6px 14px', borderRadius: 6,
                            fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                          }}>
                          {simulating[s.id] ? 'Running...' : 'Simulate'}
                        </button>
                      ) : <span style={{ color: '#9CA3AF', fontSize: 12 }}>Complete</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Session 2 admin panels: intelligence, revenue, merchants, calculator, GDL Pay */}
        <GDLAdminPanels />
      </div>
    </div>
  );

  return <GDLLayout showNav={false}>{content}</GDLLayout>;
}
